/* Beheerders management page - displays all admin users with their roles.
   Restyled: warm-neutral Buitn Planten palette (sage/bosgroen/beige), unified admin
   layout with subtitle and card frame using #e7e1d6 borders and #fcfaf6 header. */

import { useEffect, useState } from 'react';

import Breadcrumb from '@/components/admin/Breadcrumb';
import { supabase, type Admin } from '@/lib/supabase';
import { Users, Shield, Crown } from 'lucide-react';

export default function AdminsPage() {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const { data, error } = await supabase
        .from('admins')
        .select('id, email, role, created_at, updated_at, last_login')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAdmins(data || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'superadmin':
        return <Crown className="w-5 h-5 text-[#1e2b22]" />;
      case 'admin':
        return <Shield className="w-5 h-5 text-[#5a6552]" />;
      case 'moderator':
        return <Users className="w-5 h-5 text-[#7a6450]" />;
      default:
        return <Users className="w-5 h-5 text-[#5a6552]" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'bg-[#1e2b22] text-white border-[#1e2b22]';
      case 'admin':
        return 'bg-[#e7e1d6] text-[#1e2b22] border-[#cfcac2]';
      case 'moderator':
        return 'bg-[#fcfaf6] text-[#5a6552] border-[#e7e1d6]';
      default:
        return 'bg-[#fcfaf6] text-[#5a6552] border-[#e7e1d6]';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Beheerders" />
        <h1 className="text-4xl font-bold text-[#1e2b22] mb-2">Beheerders</h1>
        <p className="text-[#5a6552] mb-8">Overzicht van alle beheerdersaccounts en hun rollen</p>

        <div className="bg-white rounded-2xl border border-[#e7e1d6] overflow-hidden">
          {loading ? (
            <div className="p-10 text-center">
              <p className="text-[#5a6552]">Laden...</p>
            </div>
          ) : admins.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-[#5a6552]">Geen beheerders gevonden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Rol</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Aangemaakt</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Laatst ingelogd</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e7e1d6]">
                  {admins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-[#e7e1d6]/30 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-[#1e2b22]">{admin.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          {getRoleIcon(admin.role)}
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(admin.role)}`}>
                            {admin.role}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-[#5a6552]">{formatDate(admin.created_at)}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-[#5a6552]">
                          {admin.last_login ? formatDate(admin.last_login) : 'Nog niet ingelogd'}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
